import React, { useState, useEffect } from 'react';
import { devCost, fmtAED } from '../utils';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });

const STATUS_COLORS = {
  trial: { bg: '#fff3e0', color: '#e67e00' },
  recipe_confirmed: { bg: '#e8f5e9', color: '#2e7d32' },
  recipe_pending: { bg: '#e3f2fd', color: '#1565c0' },
  cost_sheet_confirmed: { bg: '#f3e8ff', color: '#7B2D6B' },
  launched: { bg: '#e8f5e9', color: '#1b5e20' },
  discontinued: { bg: '#fce4ec', color: '#c62828' },
};

const STATUS_LABELS = {
  trial: 'Trial',
  recipe_confirmed: 'Recipe Confirmed',
  recipe_pending: 'Recipe Pending',
  cost_sheet_confirmed: 'Cost Sheet Confirmed',
  launched: 'Launched',
  discontinued: 'Discontinued',
};

const AUTO_PALETTE = [['#ede7f6','#5e35b1'],['#e0f2f1','#00695c'],['#fff8e1','#ef6c00'],['#e1f5fe','#0277bd'],['#fce4ec','#ad1457'],['#f1f8e9','#558b2f'],['#efebe9','#4e342e']];
const autoStatusStyle = (s) => { let h = 0; for (const ch of String(s || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0; const [bg, color] = AUTO_PALETTE[h % AUTO_PALETTE.length]; return { bg, color }; };

export default function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterSfg, setFilterSfg] = useState(''); // 'fg', 'sfg', or ''
  const [filterCategory, setFilterCategory] = useState('');
  const [view, setView] = useState('grid');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      API.get('/products'),
      API.get('/projects'),
    ]).then(([p, pj]) => {
      setProducts(p.data);
      setProjects(pj.data);
      // Calculate stats
      const total = p.data.length;
      const byStatus = {};
      p.data.forEach(pr => { byStatus[pr.status] = (byStatus[pr.status]||0) + 1; });
      setStats({ total, byStatus });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const depts = [...new Set(products.map(p => p.department).filter(Boolean))];
  const groups = [...new Set(products.map(p => p.group_name || p.internal_group).filter(Boolean))];
  const categories = [...new Set(products.map(p => p.product_type).filter(Boolean))];

  const filtered = products.filter(p => {
    const searchWords = search.toLowerCase().split(/\s+/).filter(Boolean);
    const hay = [p.name, p.product_code, p.short_name, p.final_item_code, p.project_name, p.project_code].join(' ').toLowerCase();
    const matchSearch = searchWords.length === 0 || searchWords.every(w => hay.includes(w));
    const matchProject = !filterProject || p.project_id === parseInt(filterProject);
    const matchStatus = !filterStatus || p.status === filterStatus;
    const matchDept = !filterDept || p.department === filterDept;
    const matchGroup = !filterGroup || (p.group_name || p.internal_group) === filterGroup;
    const matchSfg = !filterSfg || (filterSfg === 'sfg' ? p.is_sfg === 1 : p.is_sfg !== 1);
    const matchCategory = !filterCategory || p.product_type === filterCategory;
    return matchSearch && matchProject && matchStatus && matchDept && matchGroup && matchSfg && matchCategory;
  });

  const clearFilters = () => { setSearch(''); setFilterProject(''); setFilterStatus(''); setFilterDept(''); setFilterGroup(''); setFilterSfg(''); setFilterCategory(''); };
  const hasFilters = search || filterProject || filterStatus || filterDept || filterGroup || filterSfg || filterCategory;

  if (loading) return <div className="page-loading">Loading products...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Products</div>
          <div className="page-subtitle">All NPD products across projects</div>
        </div>
      </div>

      {/* Stats Row - based on filtered products */}
      {(() => {
        const filteredByStatus = {};
        filtered.forEach(pr => { filteredByStatus[pr.status] = (filteredByStatus[pr.status]||0) + 1; });
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 20 }}>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #7B2D6B, #9d3d8a)' }}>
              <div className="stat-value" style={{ color: '#fff' }}>{filtered.length}</div>
              <div className="stat-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Total Products</div>
            </div>
            {Object.entries(filteredByStatus).map(([status, count]) => (
              <div key={status} className="stat-card" style={{ background: STATUS_COLORS[status]?.bg || '#f5f5f5' }}>
                <div className="stat-value" style={{ color: STATUS_COLORS[status]?.color || '#333' }}>{count}</div>
                <div className="stat-label">{STATUS_LABELS[status] || status}</div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Search & Filters */}
      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="text" placeholder="🔍 Search by name, item code, product code..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 2, minWidth: 200 }} />
          <select value={filterProject} onChange={e => setFilterProject(e.target.value)} style={{ flex: 1, minWidth: 140 }}>
            <option value="">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ flex: 1, minWidth: 140 }}>
            <option value="">All Statuses</option>
            {[...new Set([...Object.keys(STATUS_LABELS), ...products.map(p => p.status).filter(Boolean)])].map(v => <option key={v} value={v}>{STATUS_LABELS[v] || v}</option>)}
          </select>
          <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={{ flex: 1, minWidth: 120 }}>
            <option value="">All Depts</option>
            {depts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)} style={{ flex: 1, minWidth: 120 }}>
            <option value="">All Groups</option>
            {groups.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 4, background: 'var(--bg-elevated)', borderRadius: 8, padding: 3 }}>
            {[['', 'All'], ['fg', 'FG'], ['sfg', 'SFG']].map(([val, label]) => (
              <button key={val} className={`btn btn-sm ${filterSfg === val ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setFilterSfg(val)}
                style={{ padding: '4px 10px', fontSize: 12 }}>{label}</button>
            ))}
          </div>
          {hasFilters && <button className="btn btn-ghost btn-sm" onClick={clearFilters}>✕ Clear</button>}
          <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
            <button className={`btn btn-sm ${view==='grid'?'btn-primary':'btn-secondary'}`} onClick={() => setView('grid')}>⊞ Grid</button>
            <button className={`btn btn-sm ${view==='list'?'btn-primary':'btn-secondary'}`} onClick={() => setView('list')}>☰ List</button>
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
          Showing {filtered.length} of {products.length} products
          {hasFilters && <span style={{ marginLeft: 8, color: 'var(--accent)' }}>— filters active</span>}
        </div>
      </div>

      {/* Products Grid */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <p>No products found{hasFilters ? ' matching your filters' : ''}.</p>
          {hasFilters && <button className="btn btn-secondary btn-sm" onClick={clearFilters}>Clear Filters</button>}
        </div>
      ) : view === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {filtered.map(p => {
            const statusStyle = STATUS_COLORS[p.status] || autoStatusStyle(p.status);
            return (
              <div key={p.id} className="card" style={{ cursor: 'pointer', transition: 'all 0.15s', padding: 16 }}
                onClick={() => navigate('/products/' + p.id)}
                onMouseEnter={e => e.currentTarget.style.transform='translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform='translateY(0)'}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{p.name}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)' }}>{p.product_code || '—'}</div>
                  </div>
                  <span style={{ background: statusStyle.bg, color: statusStyle.color, padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 600, whiteSpace: 'normal', display: 'inline-block', maxWidth: 150, lineHeight: '1.25', textAlign: 'center', marginLeft: 8, flexShrink: 0 }}>
                    {STATUS_LABELS[p.status] || p.status}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                  📁 {p.project_name || '—'}
                </div>
                {p.final_item_code && (
                  <div style={{ fontSize: 11, background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px', borderRadius: 4, display: 'inline-block', marginBottom: 6 }}>
                    📦 {p.final_item_code}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                  {p.department && <span style={{ fontSize: 10, background: 'var(--bg-elevated)', padding: '2px 6px', borderRadius: 4 }}>{p.department}</span>}
                  {(p.group_name || p.internal_group) && <span style={{ fontSize: 10, background: 'var(--bg-elevated)', padding: '2px 6px', borderRadius: 4 }}>{p.group_name || p.internal_group}</span>}
                  {p.product_type && <span style={{ fontSize: 10, background: 'var(--bg-elevated)', padding: '2px 6px', borderRadius: 4 }}>{p.product_type}</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                  {p.recipe_cost > 0 && (
                    <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
                      {p.recipe_batch_kg > 0
                        ? `AED ${(p.recipe_cost / p.recipe_batch_kg).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/kg`
                        : `AED ${parseFloat(p.recipe_cost).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      }
                    </span>
                  )}
                  {(p.trial_cost > 0 || p.linked_additional_cost > 0 || p.category_dev_cost > 0) && (
                    <span style={{ color: 'var(--amber)', fontSize: 11, marginLeft: 6 }}>
                      Dev: AED {(devCost(p)).toLocaleString('en-AE', { maximumFractionDigits: 0 })}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Product Code</th>
                  <th>Product Name</th>
                  <th>Project</th>
                  <th>Department</th>
                  <th>Group</th>
                  <th>Status</th>
                  <th>ERP Code</th>
                  <th>Cost/kg</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const statusStyle = STATUS_COLORS[p.status] || autoStatusStyle(p.status);
                  return (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/products/' + p.id)}>
                      <td><span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)' }}>{p.product_code || '—'}</span></td>
                      <td style={{ fontWeight: 500 }}>{p.name}</td>
                      <td style={{ fontSize: 12 }}>{p.project_name || '—'}</td>
                      <td style={{ fontSize: 12 }}>{p.department || '—'}</td>
                      <td style={{ fontSize: 12 }}>{p.group_name || p.internal_group || '—'}</td>
                      <td><span style={{ background: statusStyle.bg, color: statusStyle.color, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'normal', display: 'inline-block', maxWidth: 150, lineHeight: '1.25', textAlign: 'center' }}>{STATUS_LABELS[p.status] || p.status}</span></td>
                      <td><span style={{ fontFamily: 'monospace', fontSize: 11 }}>{p.final_item_code || '—'}</span></td>
                      <td style={{ fontSize: 12 }}>
                        {p.recipe_cost > 0
                          ? p.recipe_batch_kg > 0
                            ? 'AED ' + (p.recipe_cost / p.recipe_batch_kg).toFixed(2) + '/kg'
                            : 'AED ' + parseFloat(p.recipe_cost).toFixed(2)
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
